import { type UncefactUnitType } from '~/types/unit';

import { type UnitsListItem } from '~/data/unit/useQueryUnits/types';

import {
  entries,
  fromEntries,
  isValidNumber,
  map,
  pipe,
} from '@vestigas/utils';

// Module-level cached units storage
let cachedUnits: Record<UncefactUnitType, UnitsListItem> | undefined;

/**
 * Sets the cached units data.
 * @param units - The units data to cache, or null to clear the cache
 */
export const setCachedUnits = (
  units: Record<UncefactUnitType, UnitsListItem> | undefined,
) => {
  if (!units) {
    return;
  }

  const uncefactUnits = pipe(
    units,
    entries,
    map(([uncefactUnit, unitDetails]) => [
      uncefactUnit.toUpperCase(),
      unitDetails,
    ]),
    fromEntries,
  );

  cachedUnits = uncefactUnits;
};

/**
 * Gets the cached units data.
 * DO NOT USE THIS UNLESS YOU ABSOLUTELY HAVE TO BECAUSE YOU ARE IN A CLASS / FUNCTION CONTEXT!!!
 * It's much better to use the useQueryUnits hooks to get the units data.
 * @returns The cached units data, or null if not cached
 * @deprecated Use useQueryUnits instead after refactoring the code that is calling this.
 */
export const getCachedUnits = () => cachedUnits;

/**
 * @deprecated The entire UnitUtils utility class is deprecated.
 * Please use the equivalent functions from the '~/utils/unit' module instead.
 */
class UnitUtils {
  round_safe = (number?: number) => {
    // null is explicitly caught because it would be parsed to 0 in this.round -> Math.round.
    // Actually, the better solution is to use TypeScript for those type checks.
    if (number === null || number === undefined) {
      return null;
    }

    const roundedNumber = Math.round(number * 100) / 100;

    if (!isValidNumber(roundedNumber)) {
      return null;
    }

    return roundedNumber;
  };

  formatDe(number?: number) {
    return Number.isNaN(number) || number === undefined
      ? ''
      : number.toLocaleString('de-DE', {
          maximumFractionDigits: 20,
          minimumFractionDigits: 0,
        });
  }

  formatDe_safe = (number?: number) => {
    // null and undefined are explicitly caught because they would throw the "properties of undefined/null" error in this.formatDe -> number.toString.
    // Actually, the better solution is to use TypeScript for those type checks.
    if (number === null) {
      return null;
    }

    return this.formatDe(number);
  };

  formatDeWithPrecision(
    number: number,
    minPrecision: number,
    maxPrecision: number,
  ) {
    return number.toLocaleString('de-DE', {
      maximumFractionDigits: maxPrecision,
      minimumFractionDigits: minPrecision,
    });
  }

  formatDeWithPrecision_safe = (
    number: number,
    minPrecision: number,
    maxPrecision: number,
  ) => {
    // null and undefined are explicitly caught because they would throw the "properties of undefined/null" error in this.formatDe -> number.toString.
    // Actually, the better solution is to use TypeScript for those type checks.
    if (number === null || number === undefined) {
      return null;
    }

    return this.formatDeWithPrecision(number, minPrecision, maxPrecision);
  };

  formatStringDe_safe(string?: string) {
    // @ts-expect-error
    if (
      string === undefined ||
      string === null ||
      string === '' ||
      Number(string) === 'NaN'
    )
      return string;

    return Number(string).toLocaleString('de-DE', {
      maximumFractionDigits: 20,
      minimumFractionDigits: 0,
    });
  }

  complexFormatDe(string: string) {
    let deNumberExpected = true;

    if (!string.includes(',') && string.includes('.')) {
      // e.g. 25.24
      deNumberExpected = false;
    }

    if (
      string.includes(',') &&
      string.includes('.') && // e.g. 25,24
      string.indexOf(',') < string.indexOf('.')
    ) {
      // e.g. 2,971.97
      deNumberExpected = false;
    }

    if (deNumberExpected) {
      if (!this.isDeNumber(string)) throw new Error('Invalid de number');

      return string.replaceAll(/[^\d,-]/g, ''); // cut out all chars that are neither a number nor a comma (e.g. 2.971,97 -> 2971,97)
    }

    if (!this.isEnNumber(string)) throw new Error('Invalid en number');

    return string.replaceAll(/[^\d.-]/g, '').replace('.', ','); // cut out all chars that are neither a number nor a dot and format to de (e.g. 2,971.97 -> 2971,97)
  }

  isDeNumber = (string: string) => {
    string = this.handleLeadingAndFollowingDelimiter(string, ',');

    if (string === '') return true;

    if (string.includes('.')) {
      return /^-?\d{1,3}(?:\.\d{3})*(?:,\d+)?$/.test(string);
    }

    return /^-?\d+,?\d*\b$/.test(string);
  };

  isEnNumber = (string: string) => {
    string = this.handleLeadingAndFollowingDelimiter(string, '.');

    if (string === '') return true;

    if (string.includes(',')) {
      return /^-?\d{1,3}(?:,\d{3})*(?:\.\d+)?$/.test(string);
    }

    return /^-?\d+\.?\d*\b$/.test(string);
  };

  handleLeadingAndFollowingDelimiter(string: string, separator: string) {
    if (string.indexOf(separator) === string.length - 1)
      string = string.slice(0, Math.max(0, string.length - 1)); // if string ends with separator, remove the separator for the validation
    if (string[0] === separator) string = `0${string}`; // add 0 to front if string starts with separator so that regex validates this as true (e.g. ,864)

    return string;
  }

  // return german number format
  // 123.4586 -> 123,46
  roundAndFormatDe = (number: number) => {
    return this.formatDe(Math.round(number * 100) / 100);
  };

  roundAndFormatDe_safe = (number: number) => {
    return this.formatDe_safe(this.round_safe(number));
  };

  // similar to roundAndFormatDe but with the addition that the precision is always exactly two decimals, e.g. 135,00
  formatDeMoneyAmount = (number) => {
    let amount = this.roundAndFormatDe(number);

    const [integers, decimals] = amount.split(',');

    if (decimals) {
      for (let index = decimals.length; index < 2; index++) {
        amount += '0';
      }
    } else {
      amount = `${integers},00`;
    }

    // add dot as thousands separator
    amount = amount.replaceAll(/\B(?=(\d{3})+(?!\d))/g, '.');

    return amount;
  };

  formatDeMoneyAmount_safe = (number: number) => {
    const amount = this.roundAndFormatDe_safe(number);

    if (amount === null) return null;

    return this.formatDeMoneyAmount(number);
  };

  // getUnitTypeOfUnit("EA") -> "amount"
  // getUnitTypeOfUnit("LTR") -> "volume"
  getUnitTypeOfUnit(standardisedUnit: UncefactUnitType) {
    const units = getCachedUnits();

    if (!units) {
      return undefined;
    }

    const key = standardisedUnit?.toUpperCase?.();

    return units[key]?.type;
  }

  // If unit isn't found in dict, take "amount" as a fallback unit type
  getUnitTypeOfUnit_safe = (standardisedUnit: UncefactUnitType) => {
    const unitType = this.getUnitTypeOfUnit(standardisedUnit);

    if (!unitType) {
      return 'amount';
    }

    return unitType;
  };

  formatValueUnitPair_safe = (
    value: number,
    unit: any,
    unitFormatter: (value: any) => any,
  ) => {
    const formattedValue = this.formatDe_safe(value);
    const formattedUnit = unitFormatter(unit);

    if (!formattedValue || !formattedUnit) {
      return null;
    }

    // OUTDATED COMMENT: needs to return a component because m3 must not be parsed to a String to render correctly
    // m3 is written as "m3" in the database, but "m³" in the UI, so it's a proper string. Yes, such characters exist.
    return (
      <>
        {formattedValue} {formattedUnit}
      </>
    );
  };

  formatValueUnitPairAsString_safe = (
    value: number,
    unit: any,
    unitFormatter: (value: any) => any,
  ) => {
    const formattedValue = this.formatDe_safe(value);
    const formattedUnit = unitFormatter(unit);

    if (!formattedValue || !formattedUnit) {
      return null;
    }

    return `${formattedValue} ${formattedUnit}`;
  };

  formatStringUnitPair_safe = (
    string: string,
    unit: any,
    unitFormatter: (value: any) => any,
  ) => {
    const formattedValue = this.formatStringDe_safe(string);
    const formattedUnit = unitFormatter(unit);

    if (!formattedValue || !formattedUnit) {
      return null;
    }

    // OUTDATED COMMENT: needs to return a component because m3 must not be parsed to a String to render correctly
    // m3 is written as "m3" in the database, but "m³" in the UI, so it's a proper string. Yes, such characters exist.
    return (
      <>
        {formattedValue} {formattedUnit}
      </>
    );
  };

  parseToNumber = (value: string | number) => {
    const number = Number(value);

    if (!isValidNumber(number)) {
      return null;
    }

    return number;
  };
}

const unitUtilsInstance = new UnitUtils();

export default unitUtilsInstance;
